import clsx from "clsx";
import { AuthContext } from "context/AuthContext";
import { CaseInfoType } from "lib/types";
import { SvgArrowDown, SvgPosition, SvgCalendar, SvgCamera } from "ui/svgs";
import { useContext, useState, useEffect } from "react";
import { showToast } from "ui/components/common";
import Modal from "react-modal";
import { StreamingCameraType } from "lib/types";
import { Popover } from "react-tiny-popover";
import { formatMinute } from "lib/utils";
import TimePick from "ui/components/timepick";
import { Input } from "antd";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import CaseItem from "ui/cases/merge-case-item";
import moment from "moment";
import { useDebouncedCallback } from "use-debounce";
import { getCaseList, addArchiveToCase } from "pages/dashboard/cases/service";

export const ArchiveModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "600px",
    borderRadius: "16px",
    borderWidth: "0px",
    padding: "24px",
  },
};
const defaultInterval = 2 * 60 * 1000;
export default function ArchiveModal({
  modalOpen,
  setModalOpen,
  startAt,
  streamingCameraPlayback,
}: {
  modalOpen: boolean;
  setModalOpen: (isOpen: boolean) => void;
  startAt: number;
  streamingCameraPlayback: StreamingCameraType;
}) {
  const { user } = useContext(AuthContext);
  const [isSTimePopoverOpen, setIsSTimePopoverOpen] = useState(false);
  const [isETimePopoverOpen, setIsETimePopoverOpen] = useState(false);

  const [archiveCreating, setArchiveCreating] = useState(false);

  const [cases, setCases] = useState<CaseInfoType[]>([]);

  const [isCaseLoading, setIsCaseLoading] = useState(true);
  const [checkedIndex, setCheckedIndex] = useState(-1);
  const [last, setLast] = useState("");
  const [hasNext, setHasNext] = useState(false);

  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);

  useEffect(() => {
    if (modalOpen) {
      return;
    }
    const date = new Date(startAt * 1000);
    date.setSeconds(0);
    setStartTime(date.getTime());
    setEndTime(date.getTime() + defaultInterval / 2);
  }, [startAt, modalOpen]);

  useEffect(() => {
    if (modalOpen) {
      setCases([]);
      setLast("");
      setCheckedIndex(-1);
      setIsCaseLoading(false);
      queryCase(user?.orgId, "", "");
    }
  }, [modalOpen]);

  const handleLoadMore = () => {
    queryCase(user?.orgId, "", last);
  };

  const queryCase = async (orgId: string, displayId: string, last: string) => {
    setIsCaseLoading(true);
    const query = `?paging.last=${last}&displayId=${displayId}&paging.limit=10&siteId=${streamingCameraPlayback?.siteId}`;
    const res = await getCaseList(orgId, query);
    setIsCaseLoading(false);
    if (res.status !== 200) {
      showToast(1, "Failed to get mergeable case list");
      return;
    }

    const data = res?.data?.data as CaseInfoType[];
    setCases(last ? [...cases, ...data] : data);
    setHasNext(res?.data?.paging?.hasNext);
    setLast(res?.data?.paging?.last);
    setCheckedIndex(res?.data?.length > 0 ? 0 : -1);
  };

  const handleInputChanged = useDebouncedCallback((text) => {
    queryCase(user?.orgId, text, "");
  }, 300);

  const handleArchive = async () => {
    setArchiveCreating(true);

    const req = {
      cameraId: +streamingCameraPlayback?.cameraId,
      startAt: Math.floor(startTime / 1000),
      endAt: Math.floor(endTime / 1000),
      streamId: 0,
      cameraName: streamingCameraPlayback?.cameraName,
    };

    const res = await addArchiveToCase(
      cases?.at(checkedIndex)?.id,
      req,
      user?.orgId,
    );
    setArchiveCreating(false);
    if (res.status !== 200) {
      showToast(1, "Failed to archive to case");
      setArchiveCreating(false);
      return;
    }
    showToast(0, "Successfully archived to case");
    setModalOpen(false);
  };

  return (
    <Modal
      isOpen={modalOpen}
      style={ArchiveModalStyles}
      contentLabel="Example Modal"
    >
      <h1 className="text-xl font-bold">Archive to Case</h1>
      <div className="p-4 bg-zinc-100 rounded-2xl mt-4 flex justify-between fill-neutral-500 text-neutral-800">
        <span className="flex items-center gap-2">
          <SvgPosition width="14" className="flex-shrink-0" />
          {streamingCameraPlayback?.siteName}
        </span>
        <span className="flex items-center gap-1">
          <SvgCamera width="18" className="flex-shrink-0" />
          {streamingCameraPlayback?.cameraName}
        </span>
        <span className="flex items-center gap-2">
          <SvgCalendar width="14" className="flex-shrink-0" />
          {moment(startAt * 1000).format("MMM DD, YYYY [at] HH:mm A")}
        </span>
      </div>
      <div className="font-semibold mt-4">
        Up to 30 minutes can be archived.
      </div>

      <div className="w-full mt-4">
        <div className="w-full flex pr-5">
          <span className="flex-grow">Start Time</span>
          <span className="flex-grow">End Time</span>
        </div>
        <div className="w-full flex mt-2 gap-4 items-center">
          <span className="flex-grow">
            <Popover
              align="start"
              isOpen={isSTimePopoverOpen}
              positions={["bottom", "right", "left", "top"]} // preferred positions by priority
              onClickOutside={() => setIsSTimePopoverOpen(false)} // handle click events outside of the popover/target here!
              content={
                <div className="border rounded-xl z-[1001]">
                  <TimePick
                    curDateTime={new Date(startTime)}
                    onChange={(date) => {
                      const startTime = date.getTime();
                      console.log("startTime", startTime);
                      setStartTime(startTime);
                    }}
                    minuteInterval={1}
                  />
                </div>
              }
            >
              <div className="h-[54px] flex items-center ">
                <button
                  className="flex flex-grow items-center justify-between rounded-2xl hover:border-blue-500 border border-neutral-400 px-5  h-full"
                  onClick={() => setIsSTimePopoverOpen(!isSTimePopoverOpen)}
                >
                  <p className="text-sm">{formatMinute(startTime)}</p>
                  <SvgArrowDown width="12" className="fill-neutral-400" />
                </button>
              </div>
            </Popover>
          </span>
          <span className="flex-grow">
            <Popover
              align="start"
              isOpen={isETimePopoverOpen}
              positions={["bottom", "right", "left", "top"]}
              onClickOutside={() => setIsETimePopoverOpen(!isETimePopoverOpen)}
              content={
                <div className="border rounded-xl ">
                  <TimePick
                    curDateTime={new Date(endTime)}
                    onChange={(date) => {
                      const endTime = date.getTime();
                      setEndTime(endTime);
                    }}
                    minuteInterval={1}
                  />
                </div>
              }
            >
              <div className="h-[54px] flex items-center ">
                <button
                  className={clsx(
                    "flex flex-grow items-center justify-between rounded-2xl hover:border-blue-500 border border-neutral-400 px-5 h-full",
                  )}
                  onClick={() => setIsETimePopoverOpen(!isETimePopoverOpen)}
                >
                  <p className="text-sm">{formatMinute(endTime)}</p>
                  <SvgArrowDown width="12" className="fill-neutral-400" />
                </button>
              </div>
            </Popover>
          </span>
        </div>
        {endTime <= startTime ? (
          <div className="text-sm text-red-500 font-semibold mt-1 ml-1">
            End time cannot be earlier than start time!
          </div>
        ) : endTime > startTime + 30 * 60 * 1000 ? (
          <div className="text-sm text-red-500 font-semibold mt-1 ml-1">
            No more than 30 minutes!
          </div>
        ) : (
          <div className="h-6" />
        )}
      </div>

      <>
        <Input
          placeholder="Search Case ID"
          className="h-[54px] rounded-2xl"
          onChange={(e) => {
            handleInputChanged(e.target.value);
          }}
          prefix={
            <MagnifyingGlassIcon className="w-4 text-gray-500 peer-focus:text-gray-900" />
          }
        />
        <div className="max-h-[440px] h-[440px] overflow-y-auto  mt-4">
          {cases.map((c, index) => (
            <CaseItem
              key={c?.displayId}
              dataCase={c}
              checked={index === checkedIndex}
              index={index}
              setCheckedCaseIndex={(i) => setCheckedIndex(i)}
            />
          ))}
          {hasNext && (
            <div className="w-full text-center mt-4">
              <button
                onClick={handleLoadMore}
                disabled={isCaseLoading}
                className="rounded-xl bg-white w-[104px] h-10 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              >
                {isCaseLoading ? (
                  <div
                    className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                    role="status"
                  ></div>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )}

          {!isCaseLoading && !hasNext && cases?.length == 0 && (
            <p className="text-center text-zinc-500">No More Case</p>
          )}
        </div>
      </>
      <div className="flex items-center justify-end mt-6">
        <button
          className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          onClick={() => {
            setModalOpen(false);
          }}
        >
          Cancel
        </button>
        <button
          disabled={
            checkedIndex === -1 ||
            endTime <= startTime ||
            archiveCreating ||
            endTime > startTime + 30 * 60 * 1000
          }
          className="ml-4 rounded-2xl flex justify-center items-center w-[113px] h-[52px] bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50  disabled:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleArchive}
        >
          {archiveCreating ? (
            <div
              className="ml-1 text-white inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
            ></div>
          ) : (
            "Archive"
          )}
        </button>
      </div>
    </Modal>
  );
}
